import { Component, EventEmitter, Input, Output, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router, ActivatedRoute } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';

import { CoreService } from '../../../services/core.service';
import { SocialPlatformI, ContentSiteI, RssFeedI } from '../../../interfaces/core.interface';
import { LatestPostsComponent } from '../../sections/latest-posts/latest-posts.component';
import { LatestAlertsComponent } from '../../sections/latest-alerts/latest-alerts.component';

type Post = {
  id: number;
  rss_title: string;
  domain_name?: string;
  image_url: string;
  post_status?: string;
  enable_repeat?: number;
  parent_post_id?: number;
  tag?: string;
  social_platform_title?: string;
  scheduled_for?: string | Date;
  rss_description?: string;
};

type Alert = {
  id: number;
  contentsite_id: number;
  alert_text: string;
  alert_type: string;
  active: number;
  platform: string;
  format: string;
  rss_img_url: string;
  rss_title: string;
  created_at: string | Date;
  Domain: string;
  posting_queue_id: number;
};

@Component({
  selector: 'app-delete-confirm-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
  ],
  template: `
    <h2 mat-dialog-title class="text-red-500">
      Confirm Delete
    </h2>

    <mat-dialog-content>
      Are you sure you want to delete this post?
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>
        Cancel
      </button>

      <button mat-button color="warn" [mat-dialog-close]="'confirm'">
        Delete
      </button>
    </mat-dialog-actions>
  `,
})
export class DeleteConfirmDialogComponent {}

@Component({
  selector: 'app-social-ai-reuse-post-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    DragDropModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatDialogModule,
    LatestPostsComponent,
    LatestAlertsComponent,
  ],
  templateUrl: './social-ai-reuse-post-form.component.html',
  styleUrl: './social-ai-reuse-post-form.component.scss',
})
export class SocialAiReusePostFormComponent implements OnInit {
  @Input() formData: any = {} as any;
  @Output() onSubmitClbk = new EventEmitter<any>();

  reuseForm!: FormGroup;
  isEditMode = false;
  isDataLoaded = false;

  companyID: number = 0;
  sites: ContentSiteI[] = [];
  platforms: SocialPlatformI[] = [];
  feeds: RssFeedI[] = [];
  filteredPlatforms: SocialPlatformI[] = [];
  filteredTags: string[] = [];
  filteredPostStatuses: { value: string; label: string }[] = [];

  selectedFile: File | null = null;
  imagePreview: string | null = null;
  uploadedFiles: { file: File | null; preview: string; alt: string }[] = [];
  private previousTag: string | null = null;

  id: string | null = null;
  urlsArray: string[] = [];
  videoFileName: string | null = null;
  aiImagePreview: string | null = null;
  videoTypes: any[] = [];

  textDirection: 'ltr' | 'rtl' = 'ltr';

  maxSidebarCards = 3;
  posts: Post[] = [];
  alerts: Alert[] = [];
  naPlaceholder = './assets/images/na.png';

  private readonly LATEST_POSTS_KEY = 'latestPosts';
  private readonly LATEST_ALERTS_KEY = 'latestAlerts';
  private readonly MAX_IMAGE_SIZE = 5 * 1024 * 1024;

  private dialog = inject(MatDialog);

  nextId: string | null = null;
  prevId: string | null = null;
  postStatus: string = '';
  enableDelete: number = 1;
  private valueChangesInitialized = false;

  videoStoryTags = [
    'fbstoryvideo',
    'igstoryvideo',
    'tiktokstoryvideo',
    'tiktokvideo',
    'igstoryreel',
    'fbstoryreel',
    'tiktokstoryreel',
  ];

  displayTagMap: Record<string, string> = {
    'Facebook Link Post': 'photolink',
    'Facebook Photo Post': 'fbphotopost',
    'Facebook Photo Post (1st comment)': 'fbfirstcomment',
    'Facebook Video Post': 'fbvideo',
    'Facebook Reel Post': 'fbreel',
    'Facebook Story Photo': 'fbstoryphoto',
    'Facebook Story Video': 'fbstoryvideo',
    'Facebook Story Reel': 'fbstoryreel',
    'Instagram Photo Post': 'igphotopost',
    'Instagram Video Post': 'igvideo',
    'Instagram Reel Post': 'igreel',
    'Instagram Carousel Post': 'igcarousel',
    'Instagram Story Video': 'igstoryvideo',
    'Instagram Story Photo': 'igstoryphoto',
    'Instagram Story Reel': 'igstoryreel',
    'TikTok Video Post': 'tiktokvideo',
    'TikTok Reel Post': 'tiktokreel',
    'TikTok Carousel Post': 'tiktokcarousel',
    'TikTok Story Photo': 'tiktokstoryphoto',
    'TikTok Story Video': 'tiktokstoryvideo',
    'TikTok Story Reel': 'tiktokstoryreel',
  };

  mediaTags = [
    'igcarousel',
    'tiktokcarousel',
    'fbreel',
    'igreel',
    'tiktokreel',
    'fbstoryreel',
    'igstoryreel',
    'tiktokstoryreel',
  ];

  singleImageTags = [
    'fbphotopost',
    'fbfirstcomment',
    'fbstoryphoto',
    'igphotopost',
    'igstoryphoto',
    'tiktokstoryphoto',
  ];

  fbTags = [
    'fbphotopost',
    'fbfirstcomment',
    'photolink',
    'fbvideo',
    'fbreel',
    'fbstoryphoto',
    'fbstoryvideo',
    'fbstoryreel',
  ];

  igTags = [
    'igphotopost',
    'igcarousel',
    'igvideo',
    'igreel',
    'igstoryphoto',
    'igstoryvideo',
    'igstoryreel',
  ];

  tiktokTags = [
    'tiktokcarousel',
    'tiktokstoryphoto',
    'tiktokvideo',
    'tiktokreel',
    'tiktokstoryvideo',
    'tiktokstoryreel',
  ];

  allVideoTags = [
    'fbvideo',
    'igvideo',
    'tiktokvideo',
    'fbstoryvideo',
    'igstoryvideo',
    'tiktokstoryvideo',
  ];

  reelTags = [
    'igreel',
    'fbreel',
    'tiktokreel',
  ];

  postStatuses = [
    { value: 'Scheduled', label: 'Scheduled' },
    { value: 'ScheduledToPostNow', label: 'Scheduled To Post Now' },
    { value: 'Draft', label: 'Draft' },
    { value: 'ScheduledToFbStory', label: 'Scheduled To Facebook Story' },
    { value: 'ScheduledToInstaStory', label: 'Scheduled To Instagram Story' },
    { value: 'ScheduledToTikTokStory', label: 'Scheduled To TikTok Story' },
  ];

  disableScheduleStatuses = [
    'Draft',
    'ScheduledToPostNow',
    'ScheduledToFbStory',
    'ScheduledToInstaStory',
    'ScheduledToTikTokStory',
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly coreService: CoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.companyID = this.coreService.user?.companyID || 0;

    if (!this.companyID) {
      this.coreService.showSnackbar('No company found.');
      return;
    }

    this.reuseForm = this.fb.group({
      site: ['', Validators.required],
      rss_feed_id: [null, Validators.required],
      platform: ['', Validators.required],
      content_site_id: [''],
      post_status: ['', Validators.required],
      scheduled_for: [null],
      post_title: ['', [Validators.required, Validators.maxLength(255)]],
      tag: ['', Validators.required],
      rss_article_url: [
        '',
        [
          Validators.required,
          Validators.pattern('^(https?:\\/\\/)?([\\w-]+\\.)+[\\w-]+(\\/\\S*)?$'),
        ],
      ],
      rss_description: ['', [Validators.required, Validators.maxLength(902)]],
      image_input: [''],
      image_array: [''],
      alt_text_array: [''],
      social_platform_id: [null],
      is_altai: [false],
      video_type: [''],
      video_type_id: [null],
      video_file: [null],
      video_url: [''],
      ai_image: [null],
    });

    this.loadSidebarPosts();
    this.loadAlertsFromStorage();

    forkJoin({
      sites: this.loadSites(),
      platforms: this.loadPlatforms(),
    }).subscribe(({ sites, platforms }) => {
      this.sites = sites as ContentSiteI[];
      this.platforms = platforms as SocialPlatformI[];

      this.route.paramMap.subscribe(pm => {
        const newId = pm.get('id');

        if (!newId) {
          return;
        }

        this.id = newId;

        this.loadActions(newId);
        this.loadPost(newId);

        this.reuseForm.get('site')?.disable();
        this.reuseForm.get('rss_feed_id')?.disable();
      });
    });
  }

  setTextDirection(dir: 'ltr' | 'rtl'): void {
    this.textDirection = dir;
  }

  getDisplayTagsForPlatform(platformTags: string[]): string[] {
    return platformTags
      .map(tag => Object.keys(this.displayTagMap).find(key => this.displayTagMap[key] === tag))
      .filter(Boolean) as string[];
  }

  private filterPostStatusesByTag(tag: string): void {
    const scheduledControl = this.reuseForm.get('scheduled_for');
    const postStatusControl = this.reuseForm.get('post_status');

    if (tag === 'igstoryphoto' || tag === 'igstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        s => s.value === 'ScheduledToInstaStory',
      );
      postStatusControl?.setValue('ScheduledToInstaStory', { emitEvent: false });
    } else if (tag === 'fbstoryphoto' || tag === 'fbstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        s => s.value === 'ScheduledToFbStory',
      );
      postStatusControl?.setValue('ScheduledToFbStory', { emitEvent: false });
    } else if (tag === 'tiktokstoryphoto' || tag === 'tiktokstoryvideo') {
      this.filteredPostStatuses = this.postStatuses.filter(
        s => s.value === 'ScheduledToTikTokStory',
      );
      postStatusControl?.setValue('ScheduledToTikTokStory', { emitEvent: false });
    } else {
      this.filteredPostStatuses = this.postStatuses.filter(
        s => ![
          'ScheduledToFbStory',
          'ScheduledToInstaStory',
          'ScheduledToTikTokStory',
        ].includes(s.value),
      );

      postStatusControl?.reset('', { emitEvent: false });
    }

    const currentStatus = postStatusControl?.value || '';

    if (this.disableScheduleStatuses.includes(currentStatus)) {
      scheduledControl?.setValue(null, { emitEvent: false });
      scheduledControl?.disable({ emitEvent: false });
    } else {
      scheduledControl?.enable({ emitEvent: false });
    }

    scheduledControl?.updateValueAndValidity({ emitEvent: false });
  }

  loadPost(id: string): void {
    this.coreService.getData(`/manual-post/editPost/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe((res: any) => {
        if (!res) {
          this.coreService.showSnackbar('Post not found.');
          return;
        }

        const videoTypeMapping: any = {
          Upload: 'video_upload',
          YouTube: 'video_url',
          AI: 'ai_video',
        };

        const mappedType = videoTypeMapping[res.video_type_name] || res.video_type_name;

        this.reuseForm.patchValue({
          video_type: mappedType,
          video_type_id: res.video_type_id ?? null,
          video_url: res.video_url || '',
        }, { emitEvent: false });

        if (res.video_type_name === 'AI') {
          this.aiImagePreview =
            res.thumbnail_file_path ||
            res.logo_file_path ||
            res.rss_img_url ||
            null;
        }

        if (mappedType === 'video_upload' && res.video_url) {
          this.videoFileName = res.video_url.split('/').pop()!;
        }

        if (mappedType === 'video_url' && res.video_url) {
          this.reuseForm.patchValue({ video_url: res.video_url }, { emitEvent: false });
        }

        this.textDirection = res.text_direction === 'rtl' ? 'rtl' : 'ltr';

        this.filteredPlatforms = this.platforms.filter(
          p => p.site_id === res.content_site_id,
        );

        const selectedPlatform = this.filteredPlatforms.find(
          (p: SocialPlatformI) => p.platform === res.platform,
        );

        const platform = (res.platform || '').toLowerCase();

        if (platform.includes('facebook')) {
          this.filteredTags = this.fbTags;
        } else if (platform.includes('instagram')) {
          this.filteredTags = this.igTags;
        } else if (platform.includes('tiktok')) {
          this.filteredTags = this.tiktokTags;
        } else {
          this.filteredTags = [];
        }

        this.filterPostStatusesByTag(res.social_tag || '');

        this.reuseForm.patchValue({
          site: res.Domain || '',
          content_site_id: res.content_site_id || '',
          rss_feed_id: res.rss_feed_id || res.id || null,
          platform: selectedPlatform?.platform || '',
          social_platform_id: selectedPlatform?.id || null,
          tag: res.social_tag || '',
          rss_description: res.rss_description || '',
          post_title: res.rss_title || '',
          rss_article_url: res.article_url || '',
          post_status: '',
          scheduled_for: null,
          alt_text_array: res.alt_text_array,
          is_altai: res.is_altai || false,
        });

        this.setRssUrlValidators(res.social_tag || '');

        this.coreService.getData(`/contentsiterssfeed/${res.content_site_id}`)
          .pipe(take(1))
          .subscribe((feedsRes: any) => {
            this.feeds = feedsRes.data || [];

            const matchedFeed = this.feeds.find(f => f.id == res.rss_feed_id);

            this.reuseForm.patchValue({
              rss_feed_id: matchedFeed ? matchedFeed.id : null,
            });
          });

        if (res.image_input_url) {
          this.imagePreview = res.image_input_url;
          this.selectedFile = null;
        }

        if (res.image_array?.length) {
          this.uploadedFiles = res.image_array.map((img: string, i: number) => ({
            file: null,
            preview: img,
            alt: res.alt_text_array?.[i] || '',
          }));

          this.urlsArray = res.image_array.map((img: string) => {
            const parts = img.split('/');
            return parts[parts.length - 1];
          });

          this.updateFormArrays();
        }

        this.isDataLoaded = true;

        if (!this.valueChangesInitialized) {
          this.initValueChanges();
          this.valueChangesInitialized = true;
        }
      });
  }

  loadVideoTypes() {
    return this.coreService.getData(`/getvideotypes`).pipe(
      take(1),
      map((res: any) => res.dataVideoTypes || []),
      catchError(() => of([])),
    );
  }

  initValueChanges(): void {
    this.reuseForm.get('post_status')?.valueChanges.subscribe((status: string) => {
      const scheduledForControl = this.reuseForm.get('scheduled_for');

      if (this.disableScheduleStatuses.includes(status)) {
        scheduledForControl?.setValue(null);
        scheduledForControl?.disable();
      } else {
        scheduledForControl?.enable();
      }

      scheduledForControl?.updateValueAndValidity();
    });

    this.reuseForm.get('video_type')?.valueChanges.subscribe(type => {
      if (!this.isDataLoaded) {
        this.reuseForm.get('video_file')?.clearValidators();
        this.reuseForm.get('video_url')?.clearValidators();
        this.reuseForm.get('ai_image')?.clearValidators();

        if (type === 'video_upload') {
          this.reuseForm.get('video_file')?.setValidators([Validators.required]);
        }

        if (type === 'video_url') {
          this.reuseForm.get('video_url')?.setValidators([
            Validators.required,
            Validators.pattern(
              /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{11}(&.*)?$/,
            ),
          ]);
        }

        if (type === 'ai_video') {
          this.reuseForm.get('ai_image')?.setValidators([Validators.required]);
        }

        this.reuseForm.get('video_file')?.updateValueAndValidity({ emitEvent: false });
        this.reuseForm.get('video_url')?.updateValueAndValidity({ emitEvent: false });
        this.reuseForm.get('ai_image')?.updateValueAndValidity({ emitEvent: false });

        return;
      }

      if (type === 'video_url') {
        this.reuseForm.patchValue(
          { video_file: null, video_url: '' },
          { emitEvent: false },
        );
        this.videoFileName = null;
      }

      if (type === 'video_upload') {
        this.reuseForm.patchValue(
          { video_url: '' },
          { emitEvent: false },
        );
      }

      if (type === 'ai_video') {
        this.reuseForm.patchValue(
          { video_file: null, video_url: '' },
          { emitEvent: false },
        );
        this.videoFileName = null;
      }

      if (this.id) {
        this.reuseForm.get('video_file')?.clearValidators();
        this.reuseForm.get('video_url')?.clearValidators();
        this.reuseForm.get('ai_image')?.clearValidators();

        if (type === 'video_upload') {
          this.reuseForm.get('video_file')?.setValidators([Validators.required]);
        }

        if (type === 'video_url') {
          this.reuseForm.get('video_url')?.setValidators([
            Validators.required,
            Validators.pattern(
              /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{11}(&.*)?$/,
            ),
          ]);
        }

        if (type === 'ai_video') {
          this.reuseForm.get('ai_image')?.setValidators([Validators.required]);
        }

        this.reuseForm.get('video_file')?.updateValueAndValidity();
        this.reuseForm.get('video_url')?.updateValueAndValidity();
        this.reuseForm.get('ai_image')?.updateValueAndValidity();

        return;
      }

      this.reuseForm.patchValue(
        {
          video_file: null,
          video_url: '',
          ai_image: null,
        },
        { emitEvent: false },
      );

      this.videoFileName = null;
      this.aiImagePreview = null;

      this.reuseForm.get('video_file')?.clearValidators();
      this.reuseForm.get('video_url')?.clearValidators();
      this.reuseForm.get('ai_image')?.clearValidators();

      if (type === 'video_upload') {
        this.reuseForm.get('video_file')?.setValidators([Validators.required]);
      }

      if (type === 'video_url') {
        this.reuseForm.get('video_url')?.setValidators([
          Validators.required,
          Validators.pattern(
            /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[A-Za-z0-9_-]{11}(&.*)?$/,
          ),
        ]);
      }

      if (type === 'ai_video') {
        this.reuseForm.get('ai_image')?.setValidators([Validators.required]);
      }

      this.reuseForm.get('video_file')?.updateValueAndValidity();
      this.reuseForm.get('video_url')?.updateValueAndValidity();
      this.reuseForm.get('ai_image')?.updateValueAndValidity();
    });

    this.reuseForm.get('platform')?.valueChanges.subscribe((platform: string) => {
      const p = (platform || '').toLowerCase();

      if (p.includes('facebook')) {
        this.filteredTags = [...this.fbTags];
      } else if (p.includes('instagram')) {
        this.filteredTags = [...this.igTags];
      } else if (p.includes('tiktok')) {
        this.filteredTags = [...this.tiktokTags];
      } else {
        this.filteredTags = [];
      }

      this.reuseForm.get('tag')?.setValue('');
      this.reuseForm.get('tag')?.markAsPristine();
      this.reuseForm.get('tag')?.markAsUntouched();
      this.previousTag = null;
    });

    this.reuseForm.get('tag')?.valueChanges.subscribe((newTag: string) => {
      this.filterPostStatusesByTag(newTag);
      this.setRssUrlValidators(newTag);

      const newIsSingle = this.singleImageTags.includes(newTag);
      const newIsMulti = this.mediaTags.includes(newTag);

      const prevIsSingle = this.previousTag
        ? this.singleImageTags.includes(this.previousTag)
        : false;

      const prevIsMulti = this.previousTag
        ? this.mediaTags.includes(this.previousTag)
        : false;

      if (!(newIsSingle && prevIsSingle) && !(newIsMulti && prevIsMulti)) {
        this.clearImages();
      }

      const newIsVideo = this.allVideoTags.includes(newTag);

      if (!newIsVideo) {
        this.reuseForm.patchValue(
          {
            video_type: '',
            video_file: null,
            video_url: '',
            ai_image: null,
          },
          { emitEvent: false },
        );

        this.videoFileName = null;
        this.aiImagePreview = null;

        this.reuseForm.get('video_type')?.clearValidators();
        this.reuseForm.get('video_file')?.clearValidators();
        this.reuseForm.get('video_url')?.clearValidators();
        this.reuseForm.get('ai_image')?.clearValidators();

        this.reuseForm.get('video_type')?.updateValueAndValidity({ emitEvent: false });
      }

      if (this.allVideoTags.includes(newTag)) {
        this.reuseForm.get('video_type')?.setValidators([Validators.required]);

        if (!this.id && !this.reuseForm.get('video_type')?.value) {
          this.reuseForm.patchValue(
            { video_type: 'video_upload' },
            { emitEvent: false },
          );
        }
      } else {
        if (!this.id) {
          this.reuseForm.get('video_type')?.clearValidators();

          this.reuseForm.patchValue({
            video_type: '',
            video_file: null,
            video_url: '',
            ai_image: null,
          }, { emitEvent: false });

          this.videoFileName = null;
          this.aiImagePreview = null;
        }
      }

      this.reuseForm.get('video_type')?.updateValueAndValidity();
      this.previousTag = newTag;
    });
  }

  onAiImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files[0]) {
      return;
    }

    const file = input.files[0];

    if (file.type === 'image/webp') {
      this.coreService.showSnackbar('WebP images are not supported. Please upload JPG or PNG.');
      input.value = '';
      return;
    }

    this.reuseForm.patchValue({ ai_image: file });
    this.reuseForm.get('ai_image')?.markAsTouched();

    const reader = new FileReader();

    reader.onload = () => {
      this.aiImagePreview = reader.result as string;
    };

    reader.readAsDataURL(file);
  }

  onVideoFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];

    this.videoFileName = file.name;

    this.reuseForm.patchValue({
      video_file: file,
      video_url: null,
    });

    this.reuseForm.get('video_file')?.markAsTouched();
  }

  private setRssUrlValidators(tag: string): void {
    const rssUrlControl = this.reuseForm.get('rss_article_url');
    const urlPattern = Validators.pattern(/^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/\S*)?$/);

    if (
      tag === 'photolink' ||
      tag === 'fbstoryphoto' ||
      tag === 'fbstoryvideo' ||
      tag === 'igstoryvideo' ||
      tag === 'igstoryphoto' ||
      tag === 'tiktokstoryphoto' ||
      tag === 'tiktokstoryvideo'
    ) {
      rssUrlControl?.setValidators([Validators.required, urlPattern]);
    } else {
      rssUrlControl?.setValidators([urlPattern]);
    }

    rssUrlControl?.updateValueAndValidity({ emitEvent: false });
  }

  loadSites(): any {
    return this.coreService.getData(`/sites-with-feeds/${this.companyID}`).pipe(
      take(1),
      map((res: any) => {
        return (res.data || []).filter((site: any) => site.hasFeed);
      }),
      catchError(() => of([])),
    );
  }

  loadPlatforms(): any {
    return this.coreService
      .getData('/getSocialPlatforms')
      .pipe(
        take(1),
        map((res: any) => (res.data as SocialPlatformI[]).filter(
          p => p.isApproved === 'Approved' && !!p.access_token,
        )),
        catchError(() => of([])),
      );
  }

  clearImages(): void {
    this.selectedFile = null;
    this.imagePreview = null;
    this.uploadedFiles.forEach(uf => URL.revokeObjectURL(uf.preview));
    this.uploadedFiles = [];
  }

  dropImages(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.uploadedFiles, event.previousIndex, event.currentIndex);
    this.updateFormArrays();
  }

  clearAll(): void {
    this.uploadedFiles = [];
    this.updateFormArrays();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files || !input.files.length) {
      return;
    }

    const file = input.files[0];

    if (file.size > this.MAX_IMAGE_SIZE) {
      this.coreService.showSnackbar(`Image "${file.name}" exceeds 5MB limit.`);
      input.value = '';
      return;
    }

    if (file.type === 'image/webp') {
      this.coreService.showSnackbar('WebP images are not supported. Please upload JPG or PNG.');
      input.value = '';
      return;
    }

    this.selectedFile = file;

    const reader = new FileReader();

    reader.onload = () => {
      this.imagePreview = reader.result as string;
    };

    reader.readAsDataURL(this.selectedFile);
  }

  removeImage(): void {
    this.selectedFile = null;
    this.imagePreview = null;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onFilesDropped(event: DragEvent): void {
    event.preventDefault();

    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  onMultipleFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (!input.files) {
      return;
    }

    Array.from(input.files).forEach(file => {
      if (file.size > this.MAX_IMAGE_SIZE) {
        this.coreService.showSnackbar(`Image "${file.name}" exceeds 5MB limit.`);
        return;
      }

      if (file.type === 'image/webp') {
        this.coreService.showSnackbar('WebP images are not supported. Please upload JPG or PNG.');
        return;
      }

      const reader = new FileReader();

      reader.onload = (e: any) => {
        this.uploadedFiles.push({
          file,
          preview: e.target.result,
          alt: '',
        });

        this.updateFormArrays();
      };

      reader.readAsDataURL(file);
    });

    input.value = '';
  }

  addFiles(files: FileList): void {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > this.MAX_IMAGE_SIZE) {
        this.coreService.showSnackbar(`Image "${file.name}" exceeds 5MB limit.`);
        continue;
      }

      if (file.type === 'image/webp') {
        this.coreService.showSnackbar('WebP images are not supported. Please upload JPG or PNG.');
        continue;
      }

      const preview = URL.createObjectURL(file);

      this.uploadedFiles.push({
        file,
        preview,
        alt: '',
      });
    }

    this.updateFormArrays();
  }

  removeFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.updateFormArrays();
  }

  updateFormArrays(): void {
    this.reuseForm.patchValue({
      image_array: this.uploadedFiles.map(f => f.file),
      alt_text_array: this.uploadedFiles.map(f => f.alt),
    });
  }

  getFilePreview(file: File): string {
    return URL.createObjectURL(file);
  }

  resetForm(): void {
    window.location.reload();
  }

  submit(): void {
    if (this.reuseForm.invalid) {
      this.reuseForm.markAllAsTouched();
      this.coreService.showSnackbar('Please complete all required fields before submitting.');
      return;
    }

    const formRaw = this.reuseForm.getRawValue();

    if (formRaw.scheduled_for) {
      const selectedDate = new Date(formRaw.scheduled_for);
      const now = new Date();

      selectedDate.setSeconds(0, 0);
      now.setSeconds(0, 0);

      if (selectedDate.getTime() < now.getTime()) {
        this.coreService.showSnackbar('The scheduled date and time cannot be in the past.');
        return;
      }
    }

    if (!formRaw.tag || formRaw.tag.trim() === '') {
      this.coreService.showSnackbar('Please select a tag before submitting.');
      return;
    }

    if (this.singleImageTags.includes(formRaw.tag)) {
      const hasSingleImage =
        !!this.selectedFile ||
        !!this.imagePreview ||
        this.uploadedFiles.length === 1;

      if (!hasSingleImage) {
        this.coreService.showSnackbar('Please select a single image for this tag.');
        return;
      }
    }

    if (this.mediaTags.includes(formRaw.tag)) {
      if (this.uploadedFiles.length < 4) {
        this.coreService.showSnackbar('You must upload at least 4 images.');
        return;
      }

      if (this.uploadedFiles.length > 10) {
        this.coreService.showSnackbar('You cannot upload more than 10 images.');
        return;
      }
    }

    let socialPlatformId = '';

    if (this.isEditMode) {
      socialPlatformId = formRaw.social_platform_id?.toString() || '';
    } else {
      const selectedPlatform = this.filteredPlatforms.find(
        (fp: SocialPlatformI) => fp.platform === formRaw.platform,
      );

      socialPlatformId = selectedPlatform?.id?.toString() || '';
    }

    const formData = new FormData();

    formData.append('company_id', this.companyID.toString());
    formData.append('content_site_id', formRaw.content_site_id?.toString() || '');
    formData.append('rss_feed_id', formRaw.rss_feed_id?.toString() || '');
    formData.append('social_platform_id', socialPlatformId);

    if (formRaw.tag?.trim()) {
      formData.append('social_tag', formRaw.tag);
    }

    if (formRaw.post_status?.trim()) {
      formData.append('post_status', formRaw.post_status);
    }

    formData.append('article_url', formRaw.rss_article_url || '');
    formData.append('rss_description', formRaw.rss_description || '');
    formData.append('post_title', formRaw.post_title || '');
    formData.append('scheduled_for', formRaw.scheduled_for || '');
    formData.append('is_altai', !!formRaw.is_altai ? '1' : '0');
    formData.append('text_direction', this.textDirection);

    if (this.id) {
      formData.append('original_post_id', this.id);
    }

    formData.append('video_type', formRaw.video_type || '');

    if (formRaw.video_type === 'video_upload' && formRaw.video_file) {
      formData.append('video_file', formRaw.video_file);
    }

    if (formRaw.video_type === 'video_url' && formRaw.video_url) {
      formData.append('video_url', formRaw.video_url);
    }

    if (formRaw.video_type === 'ai_video' && formRaw.ai_image) {
      formData.append('ai_image', formRaw.ai_image);
    }

    if (formRaw.video_type_id) {
      formData.append('video_type_id', formRaw.video_type_id.toString());
    }

    if (this.selectedFile) {
      formData.append('image_input', this.selectedFile);
    } else if (this.isEditMode && this.imagePreview) {
      formData.append('image_input', this.imagePreview);
    }

    const newFileNames: string[] = [];

    if (this.uploadedFiles.length > 0) {
      this.uploadedFiles.forEach((uf, i) => {
        if (uf.file) {
          formData.append(`image_array[${i}]`, uf.file);
          newFileNames.push(uf.file.name);
        }

        formData.append(`alt_text_array[${i}]`, uf.alt || '');
      });
    }

    const currentFiles = this.uploadedFiles
      .filter(uf => !uf.file)
      .map(uf => {
        const parts = uf.preview.split('/');
        return parts[parts.length - 1];
      });

    const urlArray = [...currentFiles, ...newFileNames];

    formData.append('url_array', JSON.stringify(urlArray));

    this.coreService.postData(`reuse/store`, formData)
      .pipe(
        take(1),
        catchError(err => {
          this.coreService.showSnackbar('An error occurred.');
          console.error(err);
          return of(null);
        }),
      )
      .subscribe(res => {
        if (res) {
          this.coreService.showSnackbar('Post reused successfully!');

          this.onSubmitClbk.emit(res);

          this.reuseForm.reset();
          this.feeds = [];
          this.filteredPlatforms = [];
          this.filteredTags = [];
          this.selectedFile = null;
          this.imagePreview = null;
          this.uploadedFiles = [];

          this.router.navigate(['/preview-post/' + res.social_tag + '/' + res.post_id]);
        }
      });
  }

  private loadSidebarPosts(): void {
    if (typeof window === 'undefined') {
      this.posts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_POSTS_KEY);

    if (!raw) {
      this.posts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Post[] | null;

      this.posts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse latest posts from localStorage', e);
      this.posts = [];
    }
  }

  private loadAlertsFromStorage(): void {
    if (typeof window === 'undefined') {
      this.alerts = [];
      return;
    }

    const raw = localStorage.getItem(this.LATEST_ALERTS_KEY);

    if (!raw) {
      this.alerts = [];
      return;
    }

    try {
      const parsed = JSON.parse(raw) as Alert[] | null;

      this.alerts = Array.isArray(parsed)
        ? parsed.slice(0, this.maxSidebarCards)
        : [];
    } catch (e) {
      console.error('Failed to parse alerts from storage', e);
      this.alerts = [];
    }
  }

  viewAlert(alert: any): void {
    const targetId = alert.posting_queue_id;

    this.router.navigate(['/preview-post', alert.format, targetId]);
  }

  viewRow(row: any): void {
    const targetId =
      row.enable_repeat === -1 ? row.parent_post_id ?? row.id : row.id;

    this.router.navigate(['/preview-post', row.tag, targetId]);
  }

  trackById = (_: number, item: Post): number => item.id;

  trackByAlertId = (_: number, item: Alert): number => item.id;

  onMainImgError(ev: Event): void {
    const img = ev.target as HTMLImageElement;

    if (!img || img.src.endsWith(this.naPlaceholder)) {
      return;
    }

    img.src = this.naPlaceholder;
  }

  platformTextClass(plat?: string): string {
    const t = (plat || '').toLowerCase().trim();

    switch (t) {
      case 'instagram':
        return 'text-fuchsia-600';

      case 'facebook':
        return 'text-blue-600';

      case 'x':
        return 'text-neutral-700';

      case 'youtube':
        return 'text-red-600';

      default:
        return 'text-gray-700';
    }
  }

  backToPrevPage(): void {
    this.location.back();
  }

  loadActions(id: string): void {
    this.coreService
      .getData(`/post/actions/${this.companyID}/${id}`)
      .pipe(take(1))
      .subscribe({
        next: (res: any) => {
          if (!res) {
            return;
          }

          this.postStatus = res.post_status ?? '';
          this.enableDelete = 1;

          this.nextId = res.next_id ?? null;
          this.prevId = res.prev_id ?? null;
        },
        error: (err: any) => {
          console.error('Failed to load post actions:', err);
        },
      });
  }

  get isDeleteDisabled(): boolean {
    const status = (this.postStatus || '').toLowerCase();

    return status !== 'draft';
  }

  nextPost(): void {
    if (!this.nextId) {
      return;
    }

    this.router.navigate(['/reuse-post', this.nextId]);
  }

  prevPost(): void {
    if (!this.prevId) {
      return;
    }

    this.router.navigate(['/reuse-post', this.prevId]);
  }

  openDeleteConfirm(): void {
    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '380px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'confirm' && this.id) {
        this.deletePost();
      }
    });
  }

  deletePost(): void {
    this.coreService.postData(`manual-post/deletePost/${this.id}`, {
      company_id: this.companyID,
    }).subscribe(() => {
      this.router.navigate(['/posts']);
    });
  }
}